import {Box, Flex, Span, P} from 'exsportia-components'
import {FC, Fragment, useCallback, useEffect, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {useNavigate} from 'react-router-dom'

import {DisplaySettingsQueries} from '../../api/display-settings'
import {MemberQueries} from '../../api/member'
import {GetVenueDisplaySettingsQueryReturnType} from '../../api/venue/get-venue-display-settings'
import {useVenue} from '../../api/venue/hooks'
import {UserService} from '../../services/user'
import {auth} from '../../utils/firebase'
import {noop} from '../../helpers/logic'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {SimpleGridLayoutHeader} from '../../layout/grid-layout/headers/simple-grid-layout-header'
import {DownloadAppModal} from '../../modals/download-app-modal/download-app-modal'
import {useAuthState} from '../../services/auth/hooks'
import {Button} from '../../ui/components/button/button'
import {ExsportiaIcon} from '../../ui/components/exsportia-icon/exsportia-icon'
import {GradientLoader} from '../../ui/components/gradient-loader/gradient-loader'
import {l} from '../../utils/i18n'
import {removeItemFromLocalStorage} from '../../utils/local-storage'
import {isNilOrEmpty} from '../../utils/logic'
import {useModalActions} from '../../utils/modals'
import {LayoutByAppearanceTemplate} from './components/layout-by-appearance-template'

const OptionButton: FC<{
  icon: string
  text: string
  navigate: () => void
}> = ({icon, text, navigate}) => (
  <Button
    onClick={navigate}
    type={'secondary'}
  >
    <Flex
      alignItems={'center'}
      justifyContent={'center'}
      gap={'8px'}
    >
      <ExsportiaIcon icon={icon} />
      <Span fontSize='16px'>{text}</Span>
    </Flex>
  </Button>
)

const defaultEntitiesOrder: GetVenueDisplaySettingsQueryReturnType['entitiesOrder'] = [
  'service',
  'class',
  'teacher',
  'justTheSpace',
  'subscription',
]

export const SelectBookingTypePage = () => {
  const navigate = useNavigate()
  // const {openModal} = useModalActions()
  const {isLoggedIn} = useAuthState()
  const {openModal} = useModalActions()
  const [backAction, setBackAction] = useState<(() => void) | null>(null)
  const [needToRefetch, setNeedToRefetch] = useState(false)
  const [showFullScheduleButton, setShowFullScheduleButton] = useState(false)
  const {data: user} = useQuery(UserService.queries.getUserQuery({cacheTime: 0, params: {id: auth.currentUser?.uid}}))
  const {data, error} = useVenue()
  const {data: member} = useQuery(
    MemberQueries.getMemberByVenueQuery({
      query: {venueId: data?.uid, userId: user?.uid},
      enabled: !!data?.uid && !!user?.uid,
      retry: false,
    }),
  )
  const isMemberBlocked = member?.isBlocked === true
  const {
    data: appearanceTemplate,
    refetch: refetchAppearanceTemplate,
    isLoading: isAppearanceTemplateLoading,
  } = useQuery(DisplaySettingsQueries.getAppearanceTemplateByIdQuery({
    params: {id: data?.appearanceTemplateId},
    staleTime: 1000 * 2
  }))
  const {data: appearanceBaseSettings, isLoading: isAppearanceBaseSettingsLoading} = useQuery(
    DisplaySettingsQueries.getAppearanceBaseSettingsQuery({
      params: {id: data?.appearanceTemplateId},
      staleTime: 1000 * 2
    }),
  )
  const isLoading = useMemo(
    () => isAppearanceTemplateLoading || isAppearanceBaseSettingsLoading,
    [isAppearanceBaseSettingsLoading, isAppearanceTemplateLoading, data],
  )
  const needDefaultView = useMemo(() => isNilOrEmpty(appearanceTemplate) && !isLoading, [appearanceTemplate, isLoading])
  useEffect(() => {
    setNeedToRefetch(true)
  }, [isLoggedIn])
  useEffect(() => {
    if (!isAppearanceTemplateLoading && needToRefetch) {
      refetchAppearanceTemplate()
      setNeedToRefetch(false)
    }
  }, [isAppearanceTemplateLoading, isLoggedIn, needToRefetch])

  useEffect(() => {
    if (error?.response?.status === 404) {
      navigate('/')
    }
  }, [error])
  const handleNavigateToServices = useCallback(() => {
    navigate('./select-service')
  }, [navigate])
  const handleNavigateToSpaces = useCallback(() => {
    navigate('./select-space-service')
  }, [navigate])
  const handleNavigateToInstructors = useCallback(() => {
    navigate('./select-instructor')
  }, [navigate])
  const handleNavigateToClass = useCallback(() => {
    navigate('./select-class')
  }, [navigate])

  const handlerOpenSubscriptionsPlug = useCallback(() => {
    openModal({
      component: <DownloadAppModal />,
      footerConfig: {disabled: true},
      title: l('subscriptions'),
    })
  }, [])
  useEffect(() => {
    removeItemFromLocalStorage('isDownloadHeaderClosed')
  }, [])

  const header = useMemo(
    () => (
      <SimpleGridLayoutHeader
        noBack={backAction === null}
        customBackAction={backAction || noop}
        title={
          data?.goLive === false
            ? l('venueUnpublished')
            : data?.entityNames.title
            ? data.entityNames.title
            : l('welcome_with_venue_name', {venueName: data?.name || 'Venue'})
        }
      />
      ),
      [data, isLoading, backAction, appearanceBaseSettings],
    )

  const optionsByType = useMemo(
    () => ({
      service: (
        <OptionButton
          text={data?.entityNames.sports || l('services')}
          icon={'shoppingCart'}
          navigate={handleNavigateToServices}
        />
      ),
      class: (
        <OptionButton
          text={data?.entityNames.classes || l('classes')}
          icon={'usersOutline24'}
          navigate={handleNavigateToClass}
        />
      ),
      teacher: (
        <OptionButton
          text={data?.entityNames.teachers || l('instructors')}
          icon={'user'}
          navigate={handleNavigateToInstructors}
        />
      ),
      justTheSpace: (
        <OptionButton
          text={data?.entityNames.areaHireOnly || l('areaHireOnly')}
          icon={'storeFrontOutline'}
          navigate={handleNavigateToSpaces}
        />
      ),
      subscription: (
        <OptionButton
          text={data?.entityNames.subscriptions || l('subscriptions')}
          icon={'crownAction'}
          navigate={handlerOpenSubscriptionsPlug}
        />
      ),
    }),
    [l, navigate, data?.entityNames, isLoading],
  )

  useEffect(() => {
    if (appearanceBaseSettings?.showSchedule && !isLoading) {
      setShowFullScheduleButton(true)
    } else {
      setShowFullScheduleButton(false)
    }
  }, [appearanceBaseSettings?.showSchedule, isLoading])

  const optionButtons: (JSX.Element | null)[] = useMemo(
    () => defaultEntitiesOrder.map((type, index) => <Fragment key={index}>{optionsByType[type]}</Fragment>),
    [optionsByType, defaultEntitiesOrder],
  )

  const showFullScheduleHandler = useCallback(() => {
    navigate('class/schedule/select-session')
  }, [navigate])

  return (
    <Box>
      <GridLayout
        withoutPadding
        header={header}
      >
        <>
          {data?.goLive === false ? (
            <Box
              m='16px'
              p='20px'
              bg='yellow_halftone'
              borderRadius='16px'
              border='1px solid notify'
            >
              <Flex
                alignItems='center'
                gap='16px'
                mb='12px'
              >
                <ExsportiaIcon
                  icon='info'
                  color='notify'
                />
                <P
                  fontWeight='bold'
                  fontSize='h2'
                >
                  {l('venueUnpublished')}
                </P>
              </Flex>
              <P
                fontSize='body'
                lineHeight='card-subtext-line-height'
              >
                {l('venueUnpublishedDesc')}
              </P>
            </Box>
          ) : needDefaultView ? (
          <Flex
            p='12px'
            gap={'16px'}
            flexDirection={'column'}
          >
            {optionButtons}
          </Flex>
        ) : (
          <Flex
            py='16px'
            gap={'16px'}
            flexDirection={'column'}
          >
            {showFullScheduleButton && !isMemberBlocked && (
              <Flex px='16px'>
                <Button onClick={showFullScheduleHandler}>{l('show_full_schedule')}</Button>
              </Flex>
            )}
            {
              appearanceTemplate ? (
                <LayoutByAppearanceTemplate
                  setBackAction={setBackAction}
                  appearanceTemplate={appearanceTemplate}
                  setShowFullScheduleButton={setShowFullScheduleButton}
                  isMemberBlocked={isMemberBlocked}
                />
              ) : (
                <Flex
                  px='16px'
                  gap='16px'
                  flexDirection='column'
                >
                  <GradientLoader height={'48px'} />
                  <GradientLoader height={'48px'} />
                  <GradientLoader height={'48px'} />
                </Flex>
              )
            }
          </Flex>
        )}
        </>
      </GridLayout>
    </Box>
  )
}
